import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { Globe, Check, Loader2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/molecules/dialog';
import type { Country } from '@/lib/types';
import type { ShippingRateFormData } from '../types/shipping-form.types';

interface ShippingRateCountriesProps {
  form: UseFormReturn<ShippingRateFormData>;
  countries: Country[] | undefined;
  isLoading: boolean;
}

export function ShippingRateCountries({ form, countries, isLoading }: ShippingRateCountriesProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const watchAvailableCountries = form.watch('availableCountries');

  const handleCountryToggle = (isoCode2: string) => {
    const current = form.getValues('availableCountries');
    if (current.includes(isoCode2)) {
      form.setValue('availableCountries', current.filter((c) => c !== isoCode2));
    } else {
      form.setValue('availableCountries', [...current, isoCode2]);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="size-5" />
          {t('shipping.availableCountries', { defaultValue: 'Available Countries' })} *
        </CardTitle>
        <CardDescription>
          {t('shipping.countriesDesc', { defaultValue: 'Select countries where this rate applies.' })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full">
              {watchAvailableCountries.length > 0
                ? `${watchAvailableCountries.length} ${t('shipping.countriesSelected', { defaultValue: 'countries selected' })}`
                : t('shipping.selectCountries', { defaultValue: 'Select countries...' })}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('shipping.selectCountries', { defaultValue: 'Select Countries' })}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
              {isLoading ? (
                <div className="col-span-full flex justify-center py-8">
                  <Loader2 className="size-6 animate-spin" />
                </div>
              ) : (
                countries?.map((country) => {
                  const isSelected = watchAvailableCountries.includes(country.isoCode2);
                  return (
                    <button
                      key={country.isoCode2}
                      type="button"
                      onClick={() => handleCountryToggle(country.isoCode2)}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <span className="text-lg">{country.flagEmoji}</span>
                      <span className="flex-1 truncate text-sm">{country.name}</span>
                      {isSelected && <Check className="size-4 text-primary shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setIsOpen(false)}>
                {t('common.done', { defaultValue: 'Done' })}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {watchAvailableCountries.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {watchAvailableCountries.slice(0, 5).map((isoCode) => {
              const country = countries?.find((c) => c.isoCode2 === isoCode);
              return (
                <Badge key={isoCode} variant="secondary" className="gap-1">
                  {country?.flagEmoji} {country?.name || isoCode}
                </Badge>
              );
            })}
            {watchAvailableCountries.length > 5 && (
              <Badge variant="outline">+{watchAvailableCountries.length - 5}</Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
